/** Layout for dashboard type pages with sidebar for navigation */
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { LoadableComponentMethods } from '@loadable/component';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { cn } from '@/utils';
import useAuth from '@/utils/auth';
import * as R from '@/Router/routes';
import ErrorNotifications from '@/components/Layouts/ErrorNotifications/ErrorNotifications';
import Footer from '@/components/Layouts/components/Footer/Footer';
import Link from '@/components/Link/Link';
import MobileHeader from '@/components/Layouts/components/MobileHeader/MobileHeader';
import Sidebar from '@/components/Layouts/components/Sidebar/Sidebar';
import SuccessNotifications from '@/components/Layouts/SuccessNotifications/SuccessNotifications';
import useMenuItems from '@/hooks/useMenuItems';

import ChevronIcon from '@/assets/icons/chevron-sharp.svg?react';
import DesktopHeader from '@/components/Layouts/components/DesktopHeader/DesktopHeader';
import useLayoutConfig from './useLayoutConfig';
import { LOGIN } from '@/Router/routes';
import { useKeycloakStore } from '@/hooks/zustandStore';
import { useLegacyLoginAuth } from '@/pages/Login/LegacyLogin/useLegacyAuth.tsx';

export type Props = {
  children: ReactNode;
  childrenTop?: ReactNode;
  className?: string;
  mobileHeaderClassName?: string;
  backPreload?: LoadableComponentMethods<unknown>['preload'];
  dataTest?: string;
  logoLinkUrl?: string;
  homePreload?: LoadableComponentMethods<unknown>['preload'];
};

const SCREENS_WIDTH_MD = [R.NEW_OFFER_INCREASED];
const SCREENS_NO_MOBILE_PADDING = [R.NEW_OFFER_INCREASED];

const Layout = ({
  children,
  childrenTop,
  className,
  mobileHeaderClassName,
  backPreload,
  dataTest,
  logoLinkUrl,
  homePreload,
}: Props) => {
  const { keycloakLogout } = useAuth();
  const { logout: legacyLogout } = useLegacyLoginAuth();
  const { pathname } = useLocation();
  const menuItems = useMenuItems({ shouldFetchMenu: false });
  const {
    mobileHeading,
    backUrl,
    hasLogout,
    hideHeader,
    isBackButtonVisible,
    isMenuVisible,
    mainStyle,
    customStyle,
    layoutBodyStyle,
    footerStyle,
  } = useLayoutConfig();
  const { t } = useTranslation('common');
  const [pending, setPending] = useState(false);

  const preload = () => {
    backPreload?.();
  };

  const isLegacyLogin = useKeycloakStore((state) => state.isLegacyLogin);
  const isLegacyLoginLoading = useKeycloakStore(
    (state) => state.isLegacyLoginLoading,
  );

  const isAuthModeResolving = isLegacyLoginLoading || isLegacyLogin === null;

  const handleLegacyLogout = useCallback(async () => {
    try {
      setPending(true);
      await legacyLogout();
    } catch {
      setPending(false);
    }
  }, [legacyLogout]);

  const handleKeycloakLogout = useCallback(() => {
    const loginUrl = `${window.location.protocol}//${window.location.host}${LOGIN}`;

    setPending(true);
    keycloakLogout(import.meta.env.VITE_STATIC_HOMEPAGE || loginUrl);
    setPending(false);
  }, []);

  const handleLogout = async () => {
    if (isAuthModeResolving) return;
    if (isLegacyLogin) {
      await handleLegacyLogout();
      return;
    }
    await handleKeycloakLogout();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const maxWidth = SCREENS_WIDTH_MD.includes(pathname)
    ? 'max-w-screen-md'
    : 'max-w-xl';
  const padding = SCREENS_NO_MOBILE_PADDING.includes(pathname)
    ? 'p-0 lg:p-4'
    : 'p-4';

  return (
    <div className="flex flex-col min-h-[100dvh]" data-test={dataTest}>
      {childrenTop}

      {!hideHeader && (
        <div className="lg:hidden">
          <MobileHeader
            className={twMerge(mobileHeaderClassName)}
            menuItems={menuItems}
            heading={mobileHeading}
            onLogout={handleLogout}
            backUrl={backUrl}
            backPreload={backPreload}
            pending={pending || isLegacyLoginLoading}
            logoLinkUrl={logoLinkUrl}
            homePreload={homePreload}
            withMenu={Boolean(isMenuVisible)}
          />
        </div>
      )}

      {!isMenuVisible && !hideHeader && (
        <div className="hidden lg:block">
          <DesktopHeader
            backUrl={backUrl}
            backPreload={backPreload}
            homePreload={homePreload}
            withLogout={hasLogout}
            logoLinkUrl={logoLinkUrl}
          />
        </div>
      )}

      <div className={cn('flex-1 flex gap-7 lg:px-10', layoutBodyStyle)}>
        {isMenuVisible && (
          <aside className="hidden lg:flex flex-col w-80 shrink-0">
            <Sidebar
              wrapperClassName="sticky top-10"
              onLogout={handleLogout}
              menuItems={menuItems}
              active={window.location.pathname}
              pending={pending || isLegacyLoginLoading}
              logoLinkUrl={logoLinkUrl}
            />
          </aside>
        )}

        <main
          className={twMerge(
            `flex flex-col relative  max-w-5xl ${padding} lg:px-0 md:py-10 w-full`,
            !isMenuVisible && `mx-auto ${maxWidth}`,
            customStyle,
            mainStyle,
            className,
          )}
        >
          <div className="sticky w-full z-10 lg:fixed md:right-5 md:top-5">
            <ErrorNotifications />
            <SuccessNotifications />
          </div>

          {isBackButtonVisible && (
            <div className="hidden lg:flex">
              <Link className="mb-10" to={backUrl} onMouseEnter={preload}>
                <ChevronIcon className="mr-2 rotate-90" />
                <span>{t('back')}</span>
              </Link>
            </div>
          )}

          {children}
        </main>
      </div>

      <Footer className={`lg:px-10 ${footerStyle}`} />
    </div>
  );
};

export default Layout;
